import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Table, InputNumber, Input, Button} from "antd";
import {SearchOutlined} from "@ant-design/icons";

export default function AdvanceOwnersTable({data, loading, selectedRowKeys, setSelectedRowKeys}) {

  const[dataSource, setDataSource] = useState(null);
  const[editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    setDataSource(data);

    // Réinitialiser la sélection lorsque les données changent
    setSelectedRowKeys([]);
  }, [data, setSelectedRowKeys]);

  /*const onSelectChange = newSelectedRowKeys => {
   console.log('selectedRowKeys changed: ', newSelectedRowKeys);
   setSelectedRowKeys(newSelectedRowKeys);
 };
 const rowSelection = {
   selectedRowKeys,
   onChange: onSelectChange,
 };*/
  const rowSelection = {
    selectedRowKeys: selectedRowKeys.map(row => row.id),
    onChange: (newSelectedRowKeys, selectedRows) => {
      // Trouver les lignes qui ont été désélectionnées
      const deselectedIds = selectedRowKeys
        .map(row => row.id)
        .filter(id => !newSelectedRowKeys.includes(id));

      // Trouver les lignes qui viennent d'être sélectionnées
      const newlySelectedIds = newSelectedRowKeys.filter(
        id => !selectedRowKeys.some(row => row.id === id)
      );

      // Mettre à jour le dataSource
      setDataSource(prevData =>
        prevData.map(item => {
          // Remettre à zéro le montant des lignes désélectionnées
          if (deselectedIds.includes(item.id)) {
            return { ...item, amount: 0 };
          }
          // Copier le solde dans amount pour les lignes nouvellement sélectionnées
          if (newlySelectedIds.includes(item.id)) {
            return { ...item, amount: parseFloat(item.solde) || 0 };
          }
          return item;
        })
      );

      // Mettre à jour selectedRowKeys avec les montants corrects
      const updatedSelectedRows = selectedRows.map(row => {
        const matchingDataItem = dataSource.find(item => item.id === row.id);
        if (newlySelectedIds.includes(row.id)) {
          return { ...row, amount: parseFloat(matchingDataItem?.solde) || 0 };
        }
        return row;
      });

      setSelectedRowKeys(updatedSelectedRows);
      //console.log(`selectedRowKeys: ${newSelectedRowKeys}`, 'selectedRows: ', updatedSelectedRows);
    },
  };

  const handleCellClick = (recordId) => {
    setEditingKey(recordId);
  };

  const handleBlur = () => {
    setEditingKey(null);
  };

  const handleAmountChange = useCallback((value, recordId) => {
    setDataSource(prevData =>
      prevData.map(item =>
        item.id === recordId
          ? { ...item, amount: value || 0 }
          : item
      )
    );

    // Sélectionner automatiquement la ligne si le montant > 0
    // Désélectionner la ligne si le montant = 0
    setSelectedRowKeys(prevSelected => {
      const updatedRow = dataSource.find(item => item.id === recordId);
      const isAlreadySelected = prevSelected.some(item => item.id === recordId);

      if (value && value > 0) {
        // Si le montant est > 0, ajouter ou mettre à jour la ligne dans la sélection
        if (isAlreadySelected) {
          return prevSelected.map(item =>
            item.id === recordId
              ? { ...item, amount: value }
              : item
          );
        } else {
          return [...prevSelected, { ...updatedRow, amount: value }];
        }
      } else {
        // Si le montant est 0, retirer la ligne de la sélection
        return prevSelected.filter(item => item.id !== recordId);
      }
    });
  }, [dataSource, setSelectedRowKeys]);

  const columns = useMemo(()=> {
    return [
      {
        title: 'Numéro',
        dataIndex: 'code',
        key: 'code',
      },
      {
        title: 'Nom',
        dataIndex: 'fullname',
        key: 'fullname',
        sorter: (a, b) => a.fullname.localeCompare(b.fullname, 'fr', { sensitivity: 'base' }),
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Rechercher un nom"
              value={selectedKeys[0]}
              onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
              onPressEnter={() => {
                confirm();
              }}
              style={{ marginBottom: 8, display: 'block' }}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="default"
                size="small"
                onClick={() => {
                  clearFilters();
                  confirm();
                }}
              >
                Réinitialiser
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  confirm();
                }}
              >
                Rechercher
              </Button>
            </div>
          </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
          record.fullname?.toString().toLowerCase().includes(value.toLowerCase()),
      },
      {
        title: 'Solde comptable',
        dataIndex: 'solde',
        key: 'solde',
        render: (value, record) => (
          parseFloat(value).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })
        ),
        align: 'right',
      },
      {
        title: 'Montant acompte',
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        render: (value, record) => {
          const isEditing = editingKey === record.id;

          if (isEditing) {
            return (
              <InputNumber
                value={value}
                onChange={(newValue) => handleAmountChange(newValue, record.id)}
                onBlur={handleBlur}
                onPressEnter={handleBlur}
                onFocus={(e) => e.target.select()}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(value) => value.replace(/\s/g, '')}
                style={{ width: '70%' }}
                precision={2}
                min={0}
                step={0.01}
                controls={false}
                autoFocus
              />
            );
          }

          return (
            <div
              onClick={() => handleCellClick(record.id)}
              style={{
                cursor: 'pointer',
                padding: '4px 11px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}
            >
              {parseFloat(value).toLocaleString("fr-FR", {
                style: "currency",
                currency: "EUR",
              })}
            </div>
          );
        }
      },
      {
        title: 'Solde restant',
        key: 'balance',
        align: 'right',
        render: (_, record) => {
          const balance = parseFloat(record.solde || 0) - parseFloat(record.amount || 0);
          return balance.toLocaleString("fr-FR", {
            style: "currency",
            currency: "EUR",
          });
        },
      },
    ];
  },[editingKey, handleAmountChange]);

  return (
    <Table
      size={"middle"}
      loading={loading}
      pagination={false}
      rowKey="id"
      rowSelection={rowSelection}
      columns={columns}
      dataSource={dataSource}
      scroll={{ y: 600 }}
      summary={pageData => {
        let totalAmount = 0;
        // Filtrer uniquement les lignes sélectionnées
        const selectedData = pageData.filter(item =>
          selectedRowKeys.some(selectedRow => selectedRow.id === item.id)
        );

        selectedData.forEach(({ amount }) => {
          totalAmount += amount ?? 0;
        });
        return (
          <Table.Summary.Row style={{
            position: 'sticky',
            bottom: 0,
            background: '#fafafa',
            zIndex: 2
          }}>
            <Table.Summary.Cell index={0} colSpan={4}>
              <b>Total des acomptes sélectionnés</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <b>{parseFloat(totalAmount).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} />
          </Table.Summary.Row>
        );
      }}
    />
  );
}